import {
  BlockingPopupModal,
  OrderableTree,
  PageWrapper,
  SaveAndCancel,
  StickyActionsHeader,
} from "@/components";
import { IOrderableTreeItem } from "@/components/OrderableTree/interfaces";
import SpinLoader from "@/containers/Layout/MainLayout/SpinLoader";
import {
  MilestoneTemplateEditor,
  ProjectTemplateCopyModal,
  TemplateDetailsEditor,
} from "@/containers/ProjectTemplate";
import { FieldNames, FormType, StatusFieldValues } from "@/enums";
import { flattenMilestones, routes } from "@/helpers";
import { getFilenameFromContentDisposition } from "@/helpers/getFileNameFromContentDisposition";
import { IMilestoneTemplate, IResults, IValues } from "@/interfaces";
import {
  ProjectTemplateProvider,
  useFormPropertyValueLookupProvider,
} from "@/providers";
import {
  useAddKeyMilestonesMutation,
  useDeleteKeyMilestonesMutation,
  useGetAdminFlatFormsQuery,
  useGetKeyMilestonesQuery,
  useGetProjectTemplateQuery,
  useSaveProjectTemplateMutation,
} from "@/services";
import { authorizationHeaders } from "@/services/helpers";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBarsSort,
  faCopy,
  faFileExport,
  faTable,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, Form, Row } from "antd";
import { saveAs } from "file-saver";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ProjectTemplateErrorModal } from "../components";
import {
  buildTreeDataRecursive,
  findMissingProductCategoriesForRequiredApprovers,
  updateMilestoneTemplateOrderingRecursive,
} from "../helpers";
import { IErrorProps } from "../interfaces";

export const ProjectTemplateEditor = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const kpis = Form.useWatch("Kpis", form);
  const vigorTemplateUrl = Form.useWatch("vigorTemplateUrl", form);
  const isCtqDisabled = Form.useWatch("isCtqDisabled", form);
  const navigate = useNavigate();
  const { id: projectTemplateId } = useParams();
  const [projectTypeId, setProjectTypeId] = useState<string>("");
  const [saveProjectTemplate] = useSaveProjectTemplateMutation();
  const [addKeyMilestones] = useAddKeyMilestonesMutation();
  const [deleteKeyMilestones] = useDeleteKeyMilestonesMutation();
  const projectType = "projectType";
  const { data: forms } = useGetAdminFlatFormsQuery({});

  const [originalMilestones, setOriginalMilestones] = useState<
    IMilestoneTemplate[]
  >([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [cancelClicked, setCancelClicked] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [milestones, setMilestones] = useState<IMilestoneTemplate[]>([]);
  const [isOrdering, setIsOrdering] = useState<boolean>(false);
  const [keyMilestonesToAdd, setKeyMilestonesToAdd] = useState<string[]>([]);
  const [keyMilestonesToDelete, setKeyMilestonesToDelete] = useState<string[]>([]);
  const [hasAppliedKeyMilestones, setHasAppliedKeyMilestones] = useState<boolean>(false);
  const [treeData, setTreeData] = useState<
    IOrderableTreeItem<IMilestoneTemplate>[]
  >([]);
  const [isActiveProjectType, setIsActiveProjectType] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<IErrorProps>({
    visible: false,
    title: "",
    description: "",
    errors: [],
  });
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();

  const {
    data: projectTemplate,
    isFetching,
    refetch: refetchProjectTemplate,
  } = useGetProjectTemplateQuery(projectTemplateId);

  const { data: keyMilestoneIds } = useGetKeyMilestonesQuery(projectTemplateId as string, { skip: !projectTemplateId });

  const setIsKeyMilestoneRecursively = (
    items: IMilestoneTemplate[],
    keyMilestoneIdSet: Set<string>
  ): IMilestoneTemplate[] =>
    items.map((item) => ({
      ...item,
      isKeyMilestone: keyMilestoneIdSet.has(item.id),
      childMilestones: item.childMilestones
        ? setIsKeyMilestoneRecursively(
          item.childMilestones,
          keyMilestoneIdSet
        )
        : item.childMilestones,
    }));

  useEffect(() => {
    if (!keyMilestoneIds || hasAppliedKeyMilestones || milestones.length === 0)
      return;

    const keyMilestoneIdSet = new Set(keyMilestoneIds);
    setMilestones((current) =>
      setIsKeyMilestoneRecursively(current, keyMilestoneIdSet)
    );
    setHasAppliedKeyMilestones(true);
  }, [keyMilestoneIds, milestones, hasAppliedKeyMilestones]);

  const handleToggleKeyMilestone = (
    milestoneId: string,
    checked: boolean
  ): void => {
    if (checked) {
      const wasPendingDelete = keyMilestonesToDelete.includes(milestoneId);
      if (wasPendingDelete) {
        setKeyMilestonesToDelete((current) =>
          current.filter((id) => id !== milestoneId)
        );
      } else {
        setKeyMilestonesToAdd((current) =>
          current.includes(milestoneId) ? current : [...current, milestoneId]
        );
      }
    } else {
      setKeyMilestonesToAdd((current) =>
        current.filter((id) => id !== milestoneId)
      );
      const wasOriginallyKeyMilestone = (keyMilestoneIds ?? []).includes(
        milestoneId
      );
      if (wasOriginallyKeyMilestone) {
        setKeyMilestonesToDelete((current) =>
          current.includes(milestoneId) ? current : [...current, milestoneId]
        );
      }
    }
  };

  const handleExport = async () => {
    if (projectTemplateId === null) return;
    setIsExporting(true);
    const headers = await authorizationHeaders(new Headers());
    const response = await fetch(
      "api/v1/projectTemplates/export?" +
      new URLSearchParams({ templateId: projectTemplateId as string }),
      {
        headers,
      }
    );
    const blob = await response.blob();
    const contentDisposition = response.headers.get("content-disposition");
    const fileName = contentDisposition
      ? getFilenameFromContentDisposition(contentDisposition)
      : "project-template.xlsx";
    saveAs(blob, fileName);
    setIsExporting(false);
  };

  useEffect(() => {
    setHasUnsavedChanges(checkForChanges(milestones));
  }, [kpis, vigorTemplateUrl, isCtqDisabled, milestones]);

  useEffect(() => {
    if (cancelClicked) {
      navigate(routes.admin.projectTemplates.list);
    }
  }, [cancelClicked]);

  useEffect(() => {
    if (!projectTemplate?.milestones) return;
    if (originalMilestones.length > 0) return;
    setOriginalMilestones(projectTemplate?.milestones);
  }, [projectTemplate?.milestones]);

  const checkForChanges = (milestones: IMilestoneTemplate[]): boolean => {
    for (let i = 0; i < milestones.length; i++) {
      const milestone = milestones[i];
      const originalMilestone = originalMilestones[i];

      if (JSON.stringify(milestone) !== JSON.stringify(originalMilestone)) {
        return true;
      }
    }

    if (
      projectTemplate?.projectKpis !== undefined &&
      kpis !== undefined &&
      JSON.stringify(projectTemplate?.projectKpis) !== JSON.stringify(kpis)
    ) {
      return true;
    }

    if (
      projectTemplate?.vigorTemplateUrl !== undefined &&
      vigorTemplateUrl !== undefined &&
      JSON.stringify(projectTemplate?.vigorTemplateUrl) !==
      JSON.stringify(vigorTemplateUrl)
    ) {
      return true;
    }

    if (
      isCtqDisabled !== undefined &&
      Boolean(projectTemplate?.isCtqDisabled) !== Boolean(isCtqDisabled)
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!projectTemplate?.milestones) return;
    setMilestones(projectTemplate.milestones);

    setProjectTypeId(projectTemplate.projectTypeId);
    setTreeData(() => buildTreeDataRecursive(projectTemplate.milestones));
  }, [projectTemplate?.milestones, projectTemplate]);

  useEffect(() => {
    if (!forms?.flatForms) return;
    const projTypes = forms.flatForms?.filter(
      (x: IResults) => x.property.name === projectType
    )[0];
    const currentType = projTypes.values.find(
      (x: IValues) => x.id === projectTypeId
    );
    setIsActiveProjectType(
      currentType?.status === StatusFieldValues.Active || !currentType?.status
    );
  }, [forms, projectTypeId]);

  const handleCloseModal = (): void => {
    setErrorModal({
      visible: false,
      title: "",
      description: "",
      errors: [],
    });
  };

  const getMilestoneErrors = (milestones: IMilestoneTemplate[]): {
    missingApprovers: string[];
    missingProjectCompleteMilestone: boolean;
  } => {
    const missingApprovers: string[] = [];
    const flattened = flattenMilestones(milestones);

    flattened
      .filter((milestone: IMilestoneTemplate) => milestone.isActive === true)
      .forEach((milestone: IMilestoneTemplate) => {
        const missingProductCategories =
          findMissingProductCategoriesForRequiredApprovers(milestone);
        if (missingProductCategories.length > 0) {
          missingApprovers.push(milestone.name);
        }
      });

    let missingProjectCompleteMilestone = false;
    if (isActiveProjectType === true) {
      const projectCompleteMilestones = flattened.filter(
        (milestone: IMilestoneTemplate) =>
          milestone.shouldCompleteProject === true &&
          milestone.isActive === true
      );
      if (projectCompleteMilestones.length === 0) {
        missingProjectCompleteMilestone = true;
      }
    }

    return {
      missingApprovers,
      missingProjectCompleteMilestone,
    };
  };

  const handleSave = (): void => {
    const updatedTemplate = _.cloneDeep({
      ...projectTemplate,
      milestones: milestones || [],
    });

    const milestoneErrors = getMilestoneErrors(updatedTemplate.milestones);
    const hasErrors =
      milestoneErrors.missingApprovers.length > 0 ||
      milestoneErrors.missingProjectCompleteMilestone;

    if (hasErrors) {
      const errorsList: string[] = [];
      let descriptionText = "";

      if (
        milestoneErrors.missingProjectCompleteMilestone &&
        milestoneErrors.missingApprovers.length > 0
      ) {
        // Both errors exist: project complete error first in errors list, then approver description (not bolded), then milestone names
        errorsList.push(
          intl.formatMessage({
            id: `entityStatusInput.noProjectCompleteMilestone`,
          })
        );
        descriptionText = intl.formatMessage({
          id: `projectTemplate.errorModal.approversRequiredErrorDescription`,
        });
        errorsList.push(...milestoneErrors.missingApprovers);
      } else if (milestoneErrors.missingProjectCompleteMilestone) {
        // Only project complete error
        descriptionText = intl.formatMessage({
          id: `entityStatusInput.noProjectCompleteMilestone`,
        });
      } else {
        // Only approver errors - use original structure
        descriptionText = intl.formatMessage({
          id: `projectTemplate.errorModal.approversRequiredErrorDescription`,
        });
        errorsList.push(...milestoneErrors.missingApprovers);
      }

      setErrorModal({
        visible: true,
        title: intl.formatMessage(
          { id: `projectTemplate.errorModal.notSavedTitle` },
          {
            projectTemplateName: getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProjectType,
              updatedTemplate?.projectTypeId
            ),
          }
        ),
        description: descriptionText,
        errors: errorsList,
      });
    } else {
      form.validateFields().then((values) => {
        updatedTemplate.projectKpis = values.Kpis;
        updatedTemplate.vigorTemplateUrl = values.vigorTemplateUrl;
        updatedTemplate.isCtqDisabled = values.isCtqDisabled ?? false;

        updatedTemplate.milestones = milestones;
        saveProjectTemplate({
          id: updatedTemplate.id,
          projectTemplate: updatedTemplate,
        })
          .unwrap()
          .then(async () => {
            if (keyMilestonesToAdd.length > 0) {
              await addKeyMilestones({
                id: updatedTemplate.id,
                milestoneTemplateIds: keyMilestonesToAdd,
              }).unwrap();
            }

            if (keyMilestonesToDelete.length > 0) {
              await deleteKeyMilestones({
                id: updatedTemplate.id,
                milestoneTemplateIds: keyMilestonesToDelete,
              }).unwrap();
            }

            setKeyMilestonesToAdd([]);
            setKeyMilestonesToDelete([]);

            toast.success(
              intl.formatMessage({ id: "general.toastChangesSaved" })
            );
            setOriginalMilestones(updatedTemplate?.milestones);
            setHasUnsavedChanges(false);
          })
          .catch((ez) =>
            toast.error(
              intl.formatMessage({ id: "general.toastSomethingWentWrong" })
            )
          );
      });
    }
  };

  const handleCancelClick = (): void => {
    setCancelClicked(true);
    setHasUnsavedChanges(false);
  };

  if (isFetching) {
    return (
      <>
        <Form form={form} component={false} />
        <SpinLoader />
      </>
    );
  }

  return (
    <>
      <BlockingPopupModal
        title={intl.formatMessage(
          { id: `projectTemplate.errorModal.notSavedTitle` },
          {
            projectTemplateName: getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProjectType,
              projectTemplate?.projectTypeId
            ),
          }
        )}
        okText={intl.formatMessage({ id: "general.yes" })}
        onOk={handleSave}
        message={intl.formatMessage({
          id: "projectTemplate.errorModal.unsavedErrorMessage",
        })}
        isBlocking={hasUnsavedChanges}
      />
      <ProjectTemplateProvider>
        <PageWrapper
          pageTitle="Project Templates"
          backButtonRoute={routes.admin.projectTemplates.list}
          pageActions={
            <StickyActionsHeader
              rightChildren={
                <>
                  <Button
                    data-test-id="export-xlsx-btn"
                    onClick={handleExport}
                    icon={<FontAwesomeIcon icon={faFileExport as IconProp} />}
                    loading={isExporting}
                  >
                    {intl.formatMessage({
                      id: "milestoneTemplates.actions.exportRoles",
                    })}
                  </Button>
                  <Button
                    data-test-id="copy"
                    onClick={() => setShowModal(true)}
                    icon={<FontAwesomeIcon icon={faCopy as IconProp} />}
                  >
                    {intl.formatMessage({
                      id: "projectTemplate.actions.copyTemplate",
                    })}
                  </Button>
                  <SaveAndCancel
                    pageName="edit-project-template"
                    saveOnClick={handleSave}
                    cancelOnClick={handleCancelClick}
                  />
                </>
              }
            />
          }
        >
          <Row className="pb-2">
            <TemplateDetailsEditor
              form={form}
              projectTemplate={projectTemplate}
            />
          </Row>
          <Card
            title={intl.formatMessage({
              id: "milestoneTemplates.actions.milestones",
            })}
            extra={
              <Button
                data-test-id="project-template-editor-reorder"
                onClick={(): void => setIsOrdering(!isOrdering)}
                icon={
                  <FontAwesomeIcon
                    icon={(isOrdering ? faTable : faBarsSort) as IconProp}
                  />
                }
              >
                {intl.formatMessage({
                  id: isOrdering ? "general.viewTable" : "general.reorder",
                })}
              </Button>
            }
          >
            {isOrdering ? (
              <OrderableTree<IMilestoneTemplate>
                data={treeData}
                handleReorder={(updatedNode, reorderedMilestones): void => {
                  const updatedMilestones =
                    updateMilestoneTemplateOrderingRecursive(
                      updatedNode,
                      reorderedMilestones,
                      milestones
                    );
                  setMilestones(updatedMilestones);
                  setTreeData(() => buildTreeDataRecursive(updatedMilestones));
                }}
              />
            ) : (
              <MilestoneTemplateEditor
                milestoneTemplates={milestones}
                setMilestoneTemplates={setMilestones}
                refetchProjectTemplate={refetchProjectTemplate}
                onToggleKeyMilestone={handleToggleKeyMilestone}
              />
            )}
          </Card>
          <ProjectTemplateCopyModal
            projectTemplate={projectTemplate}
            showModal={showModal}
            setShowModal={setShowModal}
          />
          <ProjectTemplateErrorModal
            modalState={errorModal}
            closeModal={handleCloseModal}
          />
        </PageWrapper>
      </ProjectTemplateProvider>
    </>
  );
};
